import { useState, useRef, useCallback, useEffect } from "react"

interface UseVoiceRecordingOptions {
  onChunkSent?: (partNumber: number) => void
  onError?: (error: string) => void
}

interface RecordingState {
  isRecording: boolean
  elapsed: number
  savedThrough: number
  uploadId: string | null
  parts: Array<{ etag: string; part_number: number }>
  objectKey: string | null
  deviceName: string | null
}

export function useVoiceRecording(options: UseVoiceRecordingOptions = {}) {
  const [state, setState] = useState<RecordingState>({
    isRecording: false,
    elapsed: 0,
    savedThrough: 0,
    uploadId: null,
    parts: [],
    objectKey: null,
    deviceName: null,
  })

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const partNumberRef = useRef(1)
  const uploadIdRef = useRef<string | null>(null)
  const partsRef = useRef<Array<{ etag: string; part_number: number }>>([])
  const objectKeyRef = useRef<string | null>(null)
  const startTimeRef = useRef<number>(0)
  const analyserRef = useRef<AnalyserNode | null>(null)

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const deviceName = stream.getAudioTracks()[0]?.label || null

      const audioContext = new AudioContext()
      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      analyserRef.current = analyser

      const key = `recordings/${Date.now()}-${Math.random().toString(36).slice(2)}.webm`
      objectKeyRef.current = key
      partNumberRef.current = 1
      uploadIdRef.current = null
      partsRef.current = []

      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = async (e) => {
        if (e.data.size > 0) {
          await sendChunk(e.data, key)
        }
      }

      recorder.start(5000) // chunk every 5 seconds
      startTimeRef.current = Date.now()

      timerRef.current = setInterval(() => {
        setState((prev) => ({
          ...prev,
          elapsed: Math.floor((Date.now() - startTimeRef.current) / 1000),
        }))
      }, 1000)

      setState({
        isRecording: true,
        elapsed: 0,
        savedThrough: 0,
        uploadId: null,
        parts: [],
        objectKey: key,
        deviceName,
      })
    } catch {
      options.onError?.("Microphone access denied")
    }
  }, [options])

  const stop = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop()
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
    }
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }

    const finalElapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
    setState((prev) => ({ ...prev, isRecording: false, elapsed: finalElapsed }))

    return {
      key: objectKeyRef.current,
      uploadId: uploadIdRef.current,
      parts: partsRef.current,
      elapsed: finalElapsed,
    }
  }, [])

  const cancel = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop()
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
    }
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }

    setState({
      isRecording: false,
      elapsed: 0,
      savedThrough: 0,
      uploadId: null,
      parts: [],
      objectKey: null,
      deviceName: null,
    })
  }, [])

  const getAnalyserData = useCallback(() => {
    if (!analyserRef.current) return new Uint8Array(0)
    const data = new Uint8Array(analyserRef.current.frequencyBinCount)
    analyserRef.current.getByteFrequencyData(data)
    return data
  }, [])

  async function sendChunk(blob: Blob, key: string) {
    const formData = new FormData()
    formData.append("chunk", blob)
    formData.append("key", key)
    formData.append("part_number", String(partNumberRef.current))

    try {
      const response = await fetch("/api/recordings/chunk", {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": getCSRFToken(),
        },
      })

      await response.json()

      setState((prev) => ({
        ...prev,
        savedThrough: prev.elapsed,
      }))

      partNumberRef.current += 1
      options.onChunkSent?.(partNumberRef.current - 1)
    } catch {
      options.onError?.("Failed to upload chunk")
    }
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop())
    }
  }, [])

  return {
    ...state,
    start,
    stop,
    cancel,
    getAnalyserData,
  }
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}
